// Capacitor native runtime helpers.
//
// The web app runs in three modes:
//   1. iOS Safari (or other browsers) — webkitSpeechRecognition + getUserMedia work
//   2. PWA installed to home screen — webkitSpeechRecognition silently broken on iOS
//   3. Capacitor native shell (TestFlight / App Store) — neither Web Speech nor
//      Web getUserMedia work reliably; must use native plugins
//
// `isCapacitorNative()` is the gate that flips the user experience between
// those modes. It's the ONLY place we should branch on platform — feature
// helpers below abstract the rest so calling code reads cleanly.

import { Capacitor, registerPlugin } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

// Custom native speech recognition plugin — replaces @capacitor-community/speech-recognition
// which is incompatible with Capacitor 8. Uses Apple's SFSpeechRecognizer directly.
interface NativeSpeechRecognitionPlugin {
  checkPermissions(): Promise<{ speechRecognition: string }>;
  requestPermissions(): Promise<{ speechRecognition: string }>;
  available(): Promise<{ available: boolean }>;
  start(options: { language: string; maxResults: number; partialResults: boolean; popup: boolean }): Promise<{ matches: string[] }>;
  stop(): Promise<void>;
}

const SpeechRecognition = registerPlugin<NativeSpeechRecognitionPlugin>('NativeSpeechRecognition');

/**
 * True when running inside the Capacitor iOS/Android native shell.
 * False in browsers (Safari, Chrome, etc.), even when those browsers are PWAs.
 */
export const isCapacitorNative = (): boolean => Capacitor.isNativePlatform();

/**
 * 'ios' / 'android' / 'web' — useful for platform-specific UX only when
 * `isCapacitorNative()` is true. Don't use as a substitute for `isIOS()` from
 * platform.ts, which checks the user agent and works for browser detection.
 */
export const getCapacitorPlatform = (): 'ios' | 'android' | 'web' =>
  Capacitor.getPlatform() as 'ios' | 'android' | 'web';

// ============================================================================
// CAMERA
// ============================================================================

/**
 * Open the native iOS / Android camera and return the captured photo as a File.
 * Falls back to null if not running in Capacitor (caller should use the existing
 * web CameraOverlay path instead).
 *
 * The returned File matches the shape the existing PersistentAIBar /
 * ExpandedAIChat photo pipeline expects (`processPhotoFile(file)`), so this
 * drops in cleanly without touching the downstream upload + AI analysis flow.
 *
 * Permission handling: `Camera.getPhoto()` automatically prompts the user if
 * camera permission isn't granted yet, using the iOS-native dialog with the
 * usage string from Info.plist (`NSCameraUsageDescription`). One grant per
 * install — no per-session re-prompts like Safari does.
 */
export async function takeNativePhoto(): Promise<File | null> {
  if (!isCapacitorNative()) return null;
  try {
    const photo = await Camera.getPhoto({
      source: CameraSource.Camera,
      resultType: CameraResultType.Uri,
      quality: 92,
      saveToGallery: false,
      // Don't pre-edit; the AI analysis works better with the raw frame.
      allowEditing: false,
      // 'Photo' (single) vs 'Multiple' — single is the right pattern for food logging.
      // No equivalent for "burst capture" yet.
      correctOrientation: true,
    });
    if (!photo?.webPath) return null;

    // Convert the native file:// or capacitor:// URI to a Blob → File.
    // `fetch` works with capacitor:// URIs because Capacitor's WebView intercepts them.
    const response = await fetch(photo.webPath);
    const blob = await response.blob();
    const ext = photo.format || 'jpeg';
    const file = new File([blob], `fueltron-${Date.now()}.${ext}`, {
      type: blob.type || `image/${ext}`,
    });
    return file;
  } catch (err: any) {
    // User cancelled the camera, or permission denied. Return null — the caller
    // shows no error UI for cancel; explicit denials surface via the underlying
    // `Camera.requestPermissions()` flow.
    if (err?.message?.toLowerCase?.()?.includes('cancel')) return null;
    console.error('Native camera failed:', err);
    throw err;
  }
}

/**
 * Open the native iOS / Android photo picker and return the selected photo as a File.
 * Returns null if not running in Capacitor or the user cancels.
 *
 * Used in place of <input type="file"> on native — gives a more polished UX
 * (iOS native photo grid with multiselect-disabled, search, etc.) and avoids
 * the WKWebView quirks around <input> elements.
 */
export async function pickNativePhoto(): Promise<File | null> {
  if (!isCapacitorNative()) return null;
  try {
    // Android uses the system photo picker, which needs no permission; asking for
    // 'photos' there never resolves (the storage permissions aren't in the manifest),
    // so "Choose a photo" did nothing in the Android app (2.0.0).
    if (getCapacitorPlatform() !== 'android') {
      const perms = await Camera.requestPermissions({ permissions: ['photos'] });
      if (perms.photos === 'denied') {
        throw new Error('Photo library permission denied. Go to Settings → FuelTron → Photos and allow access.');
      }
    }
    const photo = await Camera.getPhoto({
      source: CameraSource.Photos,
      resultType: CameraResultType.Uri,
      quality: 92,
      allowEditing: false,
      correctOrientation: true,
    });
    if (!photo?.webPath) return null;
    const response = await fetch(photo.webPath);
    const blob = await response.blob();
    const ext = photo.format || 'jpeg';
    return new File([blob], `fueltron-gallery-${Date.now()}.${ext}`, {
      type: blob.type || `image/${ext}`,
    });
  } catch (err: any) {
    if (err?.message?.toLowerCase?.()?.includes('cancel')) return null;
    console.error('Native photo picker failed:', err);
    throw err;
  }
}

// ============================================================================
// SPEECH RECOGNITION
// ============================================================================

/**
 * Check + request native speech recognition permission. Caches the granted
 * state in sessionStorage so we don't re-prompt within a session if iOS
 * decides to forget.
 *
 * iOS exposes TWO permissions for speech: microphone access AND speech-
 * recognition access (which uses Apple's on-device or cloud speech engine,
 * separately controlled by the user). The plugin's `requestPermissions()`
 * handles both at once.
 */
const SPEECH_PERM_CACHE_KEY = 'fueltron_native_speech_permission';

export async function ensureNativeSpeechPermission(): Promise<boolean> {
  if (!isCapacitorNative()) return false;
  const cached = sessionStorage.getItem(SPEECH_PERM_CACHE_KEY);
  if (cached === 'granted') return true;
  if (cached === 'denied') return false;

  try {
    const status = await SpeechRecognition.checkPermissions();
    if (status.speechRecognition === 'granted') {
      sessionStorage.setItem(SPEECH_PERM_CACHE_KEY, 'granted');
      return true;
    }
    const requested = await SpeechRecognition.requestPermissions();
    const ok = requested.speechRecognition === 'granted';
    sessionStorage.setItem(SPEECH_PERM_CACHE_KEY, ok ? 'granted' : 'denied');
    return ok;
  } catch (err) {
    console.error('Speech permission check failed:', err);
    return false;
  }
}

/**
 * Returns true when the native speech recognition plugin is available AND
 * the device supports it. Should be checked before showing the mic UI.
 */
export async function isNativeSpeechAvailable(): Promise<boolean> {
  if (!isCapacitorNative()) return false;
  try {
    const result = await SpeechRecognition.available();
    return !!result.available;
  } catch {
    return false;
  }
}

/**
 * One-shot native speech recognition: starts listening, returns when the
 * user finishes (silence detected) with the best transcript, or null on
 * cancel/no-speech.
 *
 * Differs from web `webkitSpeechRecognition` in important ways:
 *   - Returns a Promise (one utterance per call) instead of using event callbacks
 *   - `popup: false` — no native dialog overlay; we render our own UI in React
 *   - `partialResults: false` — we only want the final transcript, not streaming
 *
 * The caller (`useAIInput.startVoiceRecognition`) wires this into the same
 * `onResult(transcript)` callback the web flow uses, so downstream code
 * doesn't care which engine produced the transcript.
 */
export async function startNativeSpeechRecognition(): Promise<string | null> {
  if (!isCapacitorNative()) return null;
  try {
    const result = await SpeechRecognition.start({
      language: 'en-US',
      maxResults: 1,
      partialResults: false,
      popup: false,
    });
    return result?.matches?.[0] || null;
  } catch (err: any) {
    // Stopped manually via stopNativeSpeechRecognition() — not an error.
    if (err?.message?.toLowerCase?.()?.includes('stop')) return null;
    console.error('Native speech recognition failed:', err);
    throw err;
  }
}

export async function stopNativeSpeechRecognition(): Promise<void> {
  if (!isCapacitorNative()) return;
  try {
    await SpeechRecognition.stop();
  } catch {
    // Best-effort stop; safe to swallow.
  }
}

// ============================================================================
// APPLE SIGN-IN
// ============================================================================

/**
 * Trigger the native iOS Sign in with Apple sheet.
 * Returns the identity token + user info, or null if the user cancels.
 *
 * On native iOS, this uses ASAuthorizationAppleIDProvider which presents the
 * system-level Apple Sign-In sheet (Face ID / Touch ID / password). The
 * returned `identityToken` is a JWT signed by Apple with audience = bundle ID
 * (com.decisivelabsllc.fueltron), which the backend verifies via JWKS.
 *
 * IMPORTANT: Apple only sends `email` and `fullName` on the FIRST sign-in for
 * a given Apple ID + app combination. On subsequent sign-ins, those fields are
 * null. The backend must store them on first use.
 */
export interface AppleSignInResult {
  identityToken: string;
  authorizationCode: string;
  email: string | null;
  fullName: {
    givenName: string | null;
    familyName: string | null;
  } | null;
  user: string;
}

interface NativeAppleSignInPlugin {
  authorize(): Promise<{
    response: {
      user: string;
      email: string;
      givenName: string;
      familyName: string;
      identityToken: string;
      authorizationCode: string;
    };
  }>;
}

const NativeAppleSignIn = registerPlugin<NativeAppleSignInPlugin>('SignInWithApple');

export async function signInWithApple(): Promise<AppleSignInResult | null> {
  if (!isNativeIOS()) return null;
  try {
    const result = await NativeAppleSignIn.authorize();
    const r = result.response;
    if (!r.identityToken) {
      console.error('Apple Sign-In returned no identity token');
      return null;
    }
    return {
      identityToken: r.identityToken,
      authorizationCode: r.authorizationCode || '',
      email: r.email || null,
      fullName: r.givenName || r.familyName
        ? { givenName: r.givenName || null, familyName: r.familyName || null }
        : null,
      user: r.user || '',
    };
  } catch (err: any) {
    if (
      err?.message?.toLowerCase?.()?.includes('cancel') ||
      err?.code === '1001'
    ) {
      return null;
    }
    console.error('Native Apple Sign-In failed:', err);
    throw err;
  }
}

// ============================================================================
// GOOGLE SIGN-IN (iOS via ASWebAuthenticationSession)
// ============================================================================

interface NativeGoogleAuthPlugin {
  signIn(options: { apiUrl: string; startPath?: string; extraQuery?: string }): Promise<{ token: string }>;
}

const NativeGoogleAuth = registerPlugin<NativeGoogleAuthPlugin>('GoogleAuth');

export async function signInWithGoogle(apiUrl: string): Promise<string | null> {
  if (!isCapacitorNative()) return null;
  try {
    const result = await NativeGoogleAuth.signIn({ apiUrl });
    return result?.token || null;
  } catch (err: any) {
    if (
      err?.message?.toLowerCase?.()?.includes('cancel') ||
      err?.code === '1001'
    ) {
      return null;
    }
    console.error('Native Google Sign-In failed:', err);
    throw err;
  }
}

export async function linkGoogleAccount(apiUrl: string): Promise<boolean> {
  if (!isCapacitorNative()) return false;
  const jwt = localStorage.getItem('token');
  if (!jwt) return false;
  try {
    const baseUrl = apiUrl.replace(/\/api$/, '');
    const result = await NativeGoogleAuth.signIn({
      apiUrl: baseUrl,
      startPath: '/api/auth/link/google/ios-start',
      extraQuery: `token=${encodeURIComponent(jwt)}`,
    });
    return !!result?.token;
  } catch {
    return false;
  }
}

// ============================================================================
// STOREKIT 2 (IN-APP PURCHASES)
// ============================================================================

interface NativeStoreKitPlugin {
  getProducts(options: { productIds: string[] }): Promise<{
    products: Array<{
      id: string;
      title: string;
      description: string;
      price: string;
      priceValue: number;
      currency: string;
      period?: string;
    }>;
  }>;
  purchase(options: { productId: string }): Promise<{
    success: boolean;
    cancelled?: boolean;
    pending?: boolean;
    message?: string;
    transactionId?: string;
    originalTransactionId?: string;
    productId?: string;
    signedTransactionInfo?: string;
  }>;
  restorePurchases(): Promise<{
    success: boolean;
    transactions: Array<{
      transactionId: string;
      originalTransactionId: string;
      productId: string;
      signedTransactionInfo: string;
    }>;
    message: string;
  }>;
  currentEntitlements(): Promise<{
    entitlements: Array<{
      transactionId: string;
      originalTransactionId: string;
      productId: string;
      expiresDate: number;
    }>;
  }>;
}

const NativeStoreKit = registerPlugin<NativeStoreKitPlugin>('StoreKit');

export async function getStoreKitProducts(productIds: string[]): Promise<Array<{
  id: string;
  title: string;
  description: string;
  price: string;
  priceValue: number;
  currency: string;
  period?: string;
}>> {
  if (!isNativeIOS()) return [];
  try {
    const result = await NativeStoreKit.getProducts({ productIds });
    return result.products || [];
  } catch (err) {
    console.error('StoreKit getProducts failed:', err);
    return [];
  }
}

export async function purchaseStoreKitProduct(productId: string): Promise<{
  success: boolean;
  cancelled?: boolean;
  pending?: boolean;
  message?: string;
  signedTransactionInfo?: string;
  productId?: string;
}> {
  if (!isNativeIOS()) {
    return { success: false, message: 'Not running in native app' };
  }
  try {
    return await NativeStoreKit.purchase({ productId });
  } catch (err: any) {
    return { success: false, message: err?.message || 'Purchase failed' };
  }
}

export async function restoreStoreKitPurchases(): Promise<{
  success: boolean;
  transactions: Array<{ signedTransactionInfo: string; productId: string }>;
  message: string;
}> {
  if (!isNativeIOS()) {
    return { success: false, transactions: [], message: 'Not running in native app' };
  }
  try {
    return await NativeStoreKit.restorePurchases();
  } catch (err: any) {
    return { success: false, transactions: [], message: err?.message || 'Restore failed' };
  }
}

export async function getStoreKitEntitlements(): Promise<Array<{
  productId: string;
  expiresDate: number;
}>> {
  if (!isNativeIOS()) return [];
  try {
    const result = await NativeStoreKit.currentEntitlements();
    return result.entitlements || [];
  } catch {
    return [];
  }
}

// ============================================================================
// GOOGLE PLAY BILLING (Android app, PlayBillingPlugin.java)
// ============================================================================

export interface PlayProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  priceValue: number;
  currency: string;
  period: 'monthly' | 'yearly';
  freeTrial: boolean;
}

interface NativePlayBillingPlugin {
  getProducts(options: { productIds: string[] }): Promise<{ products: PlayProduct[] }>;
  purchase(options: { productId: string; accountId?: string }): Promise<{
    success: boolean; cancelled?: boolean; pending?: boolean; purchaseToken?: string; productId?: string;
  }>;
  restorePurchases(): Promise<{ purchases: Array<{ productId: string; purchaseToken: string; acknowledged: boolean }> }>;
  acknowledge(options: { purchaseToken: string }): Promise<{ acknowledged: boolean }>;
}

export const PlayBilling = registerPlugin<NativePlayBillingPlugin>('PlayBilling');

// ============================================================================
// APPLE HEALTHKIT
// ============================================================================

interface NativeHealthKitPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  requestAuthorization(): Promise<{ authorized: boolean }>;
  queryDailySummary(options: { date: string }): Promise<{
    date: string;
    steps: number;
    activeCalories: number;
    basalCalories: number;
    restingHeartRate: number;
  }>;
  querySleepAnalysis(options: { date: string; filterGarmin?: boolean }): Promise<{
    date: string;
    totalSleepSeconds: number;
    deepSleepSeconds: number;
    remSleepSeconds: number;
    coreSleepSeconds: number;
    awakeSeconds: number;
    sleepStartTime?: number;
    sleepEndTime?: number;
  }>;
  queryWorkouts(options: { startDate: string; endDate: string; filterGarmin?: boolean }): Promise<{
    workouts: Array<{
      activityType: string;
      startDate: number;
      endDate: number;
      durationSeconds: number;
      activeCalories?: number;
      distanceMeters?: number;
      sourceName: string;
      sourceBundleId: string;
    }>;
  }>;
  queryHeartRateData(options: { date: string }): Promise<{
    date: string;
    avgHeartRate: number;
    minHeartRate: number;
    maxHeartRate: number;
    hrvSDNN: number;
    restingHeartRate: number;
  }>;
  queryBodyMeasurements(): Promise<{
    weightGrams?: number;
    weightGramsDate?: number;
    bodyFatPercentage?: number;
    bodyFatPercentageDate?: number;
    vo2Max?: number;
    vo2MaxDate?: number;
  }>;
  enableBackgroundDelivery(): Promise<{ enabled: boolean; typesEnabled: number }>;
}

const NativeHealthKit = registerPlugin<NativeHealthKitPlugin>('HealthKit');

export async function isHealthKitAvailable(): Promise<boolean> {
  if (!isCapacitorNative() || getCapacitorPlatform() !== 'ios') return false;
  try {
    const result = await NativeHealthKit.isAvailable();
    return !!result.available;
  } catch {
    return false;
  }
}

export async function requestHealthKitAuthorization(): Promise<boolean> {
  if (!isCapacitorNative()) return false;
  try {
    const result = await NativeHealthKit.requestAuthorization();
    return !!result.authorized;
  } catch (err) {
    console.error('HealthKit authorization failed:', err);
    return false;
  }
}

export async function enableHealthKitBackgroundDelivery(): Promise<boolean> {
  if (!isCapacitorNative()) return false;
  try {
    const result = await NativeHealthKit.enableBackgroundDelivery();
    return !!result.enabled;
  } catch (err) {
    console.error('HealthKit background delivery failed:', err);
    return false;
  }
}

export async function queryHealthKitDailySummary(date: string) {
  if (!isCapacitorNative()) return null;
  try {
    return await NativeHealthKit.queryDailySummary({ date });
  } catch (err) {
    console.error('HealthKit daily query failed:', err);
    return null;
  }
}

export async function queryHealthKitSleep(date: string, filterGarmin = false) {
  if (!isCapacitorNative()) return null;
  try {
    return await NativeHealthKit.querySleepAnalysis({ date, filterGarmin });
  } catch (err) {
    console.error('HealthKit sleep query failed:', err);
    return null;
  }
}

export async function queryHealthKitWorkouts(startDate: string, endDate: string, filterGarmin = false) {
  if (!isCapacitorNative()) return [];
  try {
    const result = await NativeHealthKit.queryWorkouts({ startDate, endDate, filterGarmin });
    return result.workouts || [];
  } catch (err) {
    console.error('HealthKit workouts query failed:', err);
    return [];
  }
}

export async function queryHealthKitHeartRate(date: string) {
  if (!isCapacitorNative()) return null;
  try {
    return await NativeHealthKit.queryHeartRateData({ date });
  } catch (err) {
    console.error('HealthKit heart rate query failed:', err);
    return null;
  }
}

export async function queryHealthKitBodyMeasurements() {
  if (!isCapacitorNative()) return null;
  try {
    return await NativeHealthKit.queryBodyMeasurements();
  } catch (err) {
    console.error('HealthKit body measurements query failed:', err);
    return null;
  }
}

// ============================================================================
// HAPTICS
// ============================================================================

/**
 * Vibrate the device with a light impact (e.g. shutter capture, button tap).
 * No-op on web — `navigator.vibrate` doesn't exist on iOS Safari and is
 * inconsistent on Android browsers, so we don't bother trying.
 */
export async function nativeHapticImpact(style: 'light' | 'medium' | 'heavy' = 'light'): Promise<void> {
  if (!isCapacitorNative()) return;
  try {
    const styleMap = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    await Haptics.impact({ style: styleMap[style] });
  } catch {
    // Silent — haptic feedback is purely cosmetic; failure shouldn't break flow.
  }
}

// ============================================================================
// PUSH NOTIFICATIONS (iOS native → APNs, Android native → FCM)
// ============================================================================
//
// Web push (service worker + VAPID) does not exist inside WKWebView, so the
// iOS app registers with APNs through @capacitor/push-notifications and hands
// the device token to POST /notifications/register-token with platform:'ios'.
// The backend then delivers coaching, streak-guard and Free-Pro-Day pushes
// through apnsService. The Android app does the same with Firebase (FCM token,
// kind:'fcm', delivered by fcmService) — only in builds made with
// google-services.json, which set REACT_APP_ANDROID_FCM=true; without Firebase,
// register() would crash the app. Everything here is a no-op in browsers.

const NATIVE_PUSH_GRANTED_KEY = 'ft_native_push_granted';
const NATIVE_PUSH_LAST_REGISTERED_KEY = 'ft_native_push_registered_at';

export const isNativeIOS = (): boolean => isCapacitorNative() && getCapacitorPlatform() === 'ios';
/** The native Android app (Capacitor), which replaced the Play TWA. isTWA() is false in it. */
export const isNativeAndroid = (): boolean => isCapacitorNative() && getCapacitorPlatform() === 'android';

/** This app build can receive native pushes (always on iPhone; on Android only when built with Firebase). */
export const nativePushAvailable = (): boolean =>
  isNativeIOS() || (isNativeAndroid() && process.env.REACT_APP_ANDROID_FCM === 'true');

/** 'granted' | 'denied' | 'prompt' | 'unsupported' */
export async function checkNativePushPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unsupported'> {
  if (!nativePushAvailable()) return 'unsupported';
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const status = await PushNotifications.checkPermissions();
    const r = status.receive;
    if (r === 'granted') return 'granted';
    if (r === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'unsupported';
  }
}

/**
 * Ask for permission (if needed), register with APNs, and send the device
 * token to the backend. Resolves true once the token is stored server-side.
 * Safe to call on every launch: APNs tokens can rotate, and re-registering
 * is cheap.
 */
export async function registerNativePush(apiUrl: string): Promise<boolean> {
  if (!nativePushAvailable()) return false;
  const jwt = localStorage.getItem('token');
  if (!jwt) return false;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      localStorage.removeItem(NATIVE_PUSH_GRANTED_KEY);
      return false;
    }
    localStorage.setItem(NATIVE_PUSH_GRANTED_KEY, '1');
    // Android 8+ shows a notification only on a channel; fcmService sends to this one.
    if (isNativeAndroid()) {
      await PushNotifications.createChannel({ id: 'fueltron_default', name: 'Coach and reminders', importance: 4 }).catch(() => {});
    }

    // register() resolves before the token arrives; the token comes via the
    // 'registration' event (bridged from AppDelegate's didRegister... callback).
    const token = await new Promise<string | null>((resolve) => {
      let settled = false;
      const finish = (v: string | null) => { if (!settled) { settled = true; resolve(v); } };
      const timer = setTimeout(() => finish(null), 15000);
      Promise.all([
        PushNotifications.addListener('registration', (t) => { clearTimeout(timer); finish(t?.value || null); }),
        PushNotifications.addListener('registrationError', () => { clearTimeout(timer); finish(null); }),
      ]).then(() => PushNotifications.register()).catch(() => finish(null));
    });
    if (!token) return false;

    const res = await fetch(`${apiUrl}/notifications/register-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        token,
        platform: isNativeAndroid() ? 'android' : 'ios',
        ...(isNativeAndroid() ? { kind: 'fcm' } : {}),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      }),
    });
    if (!res.ok) return false;
    localStorage.setItem(NATIVE_PUSH_LAST_REGISTERED_KEY, String(Date.now()));
    return true;
  } catch (err) {
    console.warn('Native push registration failed:', err);
    return false;
  }
}

/** True when the token was (re)sent to the server within the last 12 hours. */
export function nativePushRecentlyRegistered(): boolean {
  const at = Number(localStorage.getItem(NATIVE_PUSH_LAST_REGISTERED_KEY) || 0);
  return Date.now() - at < 12 * 60 * 60 * 1000;
}

/**
 * Listen for the user tapping a push. The backend puts a `url` in the custom
 * payload (e.g. '/scan', '/coach'); the callback receives it so the caller can
 * route with React Router. Returns a cleanup function.
 */
export async function onNativePushTap(cb: (url: string | null, data: any) => void): Promise<() => void> {
  if (!nativePushAvailable()) return () => {};
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const handle = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data: any = action?.notification?.data || {};
      cb(typeof data.url === 'string' ? data.url : null, data);
    });
    return () => { handle.remove(); };
  } catch {
    return () => {};
  }
}

// ============================================================================
// APP STORE RATING PROMPT
// ============================================================================

interface NativeStoreKitReviewPlugin {
  requestReview(): Promise<void>;
}
const StoreKitReview = registerPlugin<NativeStoreKitReviewPlugin>('StoreKit');
// Android: AppReviewPlugin.java (Play in-app review).
const PlayReview = registerPlugin<NativeStoreKitReviewPlugin>('AppReview');

const REVIEW_SCANS_KEY = 'ft_review_confirmed_scans';
const REVIEW_LAST_PROMPT_KEY = 'ft_review_prompted_at';
const REVIEW_MIN_SCANS = 3;
const REVIEW_COOLDOWN_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Ask iOS to show the system "Enjoying FuelTron?" rating sheet, but only at a
 * win moment: after the user's 3rd confirmed photo scan, then no more than
 * once every 90 days. iOS itself decides whether to actually display it
 * (Apple caps prompts at 3 per 365 days), so this is a request, not a
 * guarantee. No-op off iOS.
 */
export function maybeRequestAppReview(): void {
  if (!isNativeIOS() && !isNativeAndroid()) return;
  try {
    const scans = Number(localStorage.getItem(REVIEW_SCANS_KEY) || 0) + 1;
    localStorage.setItem(REVIEW_SCANS_KEY, String(scans));
    if (scans < REVIEW_MIN_SCANS) return;
    const last = Number(localStorage.getItem(REVIEW_LAST_PROMPT_KEY) || 0);
    if (Date.now() - last < REVIEW_COOLDOWN_MS) return;
    localStorage.setItem(REVIEW_LAST_PROMPT_KEY, String(Date.now()));
    // Let the "Meal logged!" state land before the system sheet appears.
    setTimeout(() => {
      (isNativeAndroid() ? PlayReview.requestReview() : StoreKitReview.requestReview()).catch(() => {});
    }, 1500);
  } catch {
    // never let a rating prompt break the scan flow
  }
}
